import type {
	ExtensionScreenPlacements,
	PlacementSimulationConfig,
	SimulationConfigState,
	SimulationParams,
} from './types';

const EMPTY_STATE: SimulationConfigState = { placements: {} };
const EMPTY_SCREEN_PLACEMENTS: ExtensionScreenPlacements = {
	currentlyMounted: [],
	hasMounted: [],
};

const cloneSimulationParams = (params: SimulationParams): SimulationParams =>
	Object.fromEntries(
		Object.entries(params).map(([key, value]) => [key, Array.isArray(value) ? [...value] : value]),
	);

const clonePlacementSimulationConfig = (
	config: PlacementSimulationConfig,
): PlacementSimulationConfig => ({
	enabled: config.enabled,
	params: cloneSimulationParams(config.params),
});

const cloneState = (next: SimulationConfigState): SimulationConfigState => ({
	placements: Object.fromEntries(
		Object.entries(next.placements).map(([placementId, config]) => [
			placementId,
			clonePlacementSimulationConfig(config),
		]),
	),
});

const cloneScreenPlacements = (
	next: ExtensionScreenPlacements,
): ExtensionScreenPlacements => ({
	currentlyMounted: [...next.currentlyMounted],
	hasMounted: [...next.hasMounted],
});

const areScreenPlacementsEqual = (
	a: ExtensionScreenPlacements,
	b: ExtensionScreenPlacements,
): boolean =>
	a.currentlyMounted.length === b.currentlyMounted.length &&
	a.hasMounted.length === b.hasMounted.length &&
	a.currentlyMounted.every((id, index) => id === b.currentlyMounted[index]) &&
	a.hasMounted.every((id, index) => id === b.hasMounted[index]);

export class ExtensionBridgeStore {
	private state: SimulationConfigState = EMPTY_STATE;
	private screenPlacements: ExtensionScreenPlacements = EMPTY_SCREEN_PLACEMENTS;
	private readonly listeners = new Set<() => void>();

	getSimulationConfig(): SimulationConfigState {
		return cloneState(this.state);
	}

	getPlacementSimulationConfig(placementId: string): PlacementSimulationConfig | undefined {
		const entry = this.state.placements[placementId];
		return entry ? clonePlacementSimulationConfig(entry) : undefined;
	}

	/**
	 * Returns simulate params for a placement only when that placement's toggle is
	 * on. Disabled or missing placements return `undefined` so callers can leave
	 * the request untouched.
	 */
	getEnabledSimulationParams(placementId: string): SimulationParams | undefined {
		const params = this.getEnabledSimulationParamsSnapshot(placementId);
		return params ? cloneSimulationParams(params) : undefined;
	}

	/**
	 * Returns the store-owned params reference for React's external-store snapshot.
	 * This is intentionally not re-exported from the bridge's public entry point:
	 * public callers must receive a clone so they cannot mutate store state without
	 * notifying subscribers.
	 */
	getEnabledSimulationParamsSnapshot(placementId: string): SimulationParams | undefined {
		const entry = this.state.placements[placementId];
		if (!entry?.enabled) {
			return undefined;
		}
		return entry.params;
	}

	setPlacementSimulationConfig(placementId: string, config: PlacementSimulationConfig): void {
		this.state = {
			placements: {
				...this.state.placements,
				[placementId]: clonePlacementSimulationConfig(config),
			},
		};
		this.notify();
	}

	clearPlacementSimulationConfig(placementId: string): void {
		if (!(placementId in this.state.placements)) {
			return;
		}

		const { [placementId]: _removed, ...placements } = this.state.placements;
		this.state = { placements };
		this.notify();
	}

	resetSimulationConfig(): void {
		if (Object.keys(this.state.placements).length === 0) {
			return;
		}
		this.state = EMPTY_STATE;
		this.notify();
	}

	getScreenPlacementsForExtension(): ExtensionScreenPlacements {
		return cloneScreenPlacements(this.screenPlacements);
	}

	/**
	 * Mirrors Post Office screen-context placement IDs for the extension bridge.
	 * Notifies subscribers when the on-screen snapshot changes.
	 */
	setScreenPlacementsForExtension(placements: ExtensionScreenPlacements): void {
		const next = cloneScreenPlacements(placements);
		if (areScreenPlacementsEqual(this.screenPlacements, next)) {
			return;
		}
		this.screenPlacements = next;
		this.notify();
	}

	resetScreenPlacementsForExtension(): void {
		if (areScreenPlacementsEqual(this.screenPlacements, EMPTY_SCREEN_PLACEMENTS)) {
			return;
		}
		this.screenPlacements = EMPTY_SCREEN_PLACEMENTS;
		this.notify();
	}

	subscribeSimulationConfig(listener: () => void): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	private notify(): void {
		this.listeners.forEach((listener) => listener());
	}
}

export const extensionBridgeStore: ExtensionBridgeStore = new ExtensionBridgeStore();
